import { FlexBox, Icon, Spinner, Text } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import { HTMLAttributes } from 'react';

import { AppLink } from 'components/appLink';

import { AnalyticsSidenavItem as AnalyticsSidenavItemT } from '../../analyticsSidenav.types';

import styles from './analyticsSidenavItem.module.scss';

type AnalyticsSidenavItemProps = {
  item: AnalyticsSidenavItemT;
};

export const AnalyticsSidenavItem = ({
  item,
}: AnalyticsSidenavItemProps): JSX.Element => {
  const renderItem = (): JSX.Element => {
    const baseProps: HTMLAttributes<HTMLElement> = {
      className: classNames(styles.item, {
        [styles.itemActive]: item.active,
        [styles.itemLink]: item.type === 'link',
        [styles.itemButton]: item.type === 'button',
      }),
      role: 'menuitem',
    };

    const iconAndTitle = (
      <>
        {item.icon && (
          <FlexBox role="img" aria-label={item.title}>
            {item.type === 'accordion' && item.options.isLoading ? (
              <Spinner size={20} />
            ) : (
              <Icon
                className={styles.icon}
                name={item.icon}
                color={colors.neutral['700']}
                size={20}
              />
            )}
          </FlexBox>
        )}

        <Text.Paragraph className={styles.title} variant="1">
          {item.title}
        </Text.Paragraph>
      </>
    );

    switch (item.type) {
      default:
      case 'accordion':
        return (
          <div {...baseProps}>
            {iconAndTitle} {item.options.action}
          </div>
        );

      case 'link':
        return (
          <AppLink {...baseProps} {...item.options}>
            {iconAndTitle}
          </AppLink>
        );

      case 'button':
        return (
          <button {...baseProps} {...item.options}>
            {iconAndTitle}
          </button>
        );
    }
  };

  const renderSubItems = (): JSX.Element | null => {
    if (item.type !== 'accordion') {
      return null;
    }

    const { subItems, isLoading, emptyState } = item.options;

    if (!isLoading && !subItems.length) {
      return (
        <FlexBox justifyContent="center" padding="200">
          {emptyState}
        </FlexBox>
      );
    }

    return (
      <FlexBox flexDirection="column" gap="100" className={styles.subItems}>
        {item.options.subItems.map((subItem) => {
          return (
            <AppLink
              key={subItem.title}
              className={classNames(styles.subItem, {
                [styles.subItemActive]: subItem.active,
              })}
              {...subItem.options}
            >
              <Text.Paragraph variant="2">{subItem.title}</Text.Paragraph>
            </AppLink>
          );
        })}
      </FlexBox>
    );
  };

  return (
    <>
      {renderItem()}
      {renderSubItems()}
    </>
  );
};
